import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import { CheckCircle, Error, PersonAdd, Business } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { gamoraApi } from '../services/api';

interface InvitationDetails {
  organization: {
    id: string;
    name: string;
    description?: string;
    industry?: string;
  };
  role: string;
  invited_by: string;
  message?: string;
  expires_at: string;
}

const InvitationAccept: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [expired, setExpired] = useState(false);

  const loadInvitationDetails = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Get invitation details from the backend
      const response = await gamoraApi.getInvitationDetails(token);
      setInvitation(response);
      
      // Check if invitation is expired
      const expiryDate = new Date(response.expires_at);
      if (new Date() > expiryDate) {
        setExpired(true);
      }
    } catch (err: any) {
      console.error('Failed to load invitation:', err);
      if (err.response?.status === 404) {
        setError('Invitation not found or has expired');
      } else if (err.response?.status === 400) {
        setError(err.response.data?.detail || 'Invalid invitation');
      } else {
        setError('Failed to load invitation details');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadInvitationDetails();
    }
  }, [token, loadInvitationDetails]);

  const handleAcceptInvitation = async () => {
    if (!token) return;
    
    try {
      setProcessing(true);
      setError(null);
      
      await gamoraApi.acceptInvitation(token);
      setSuccess(true);
      
      // Redirect to main app after a short delay
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err: any) {
      console.error('Failed to accept invitation:', err);
      if (err.response?.status === 400) {
        setError(err.response.data?.detail || 'Failed to accept invitation');
      } else {
        setError('Failed to accept invitation. Please try again.');
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleDeclineInvitation = async () => {
    if (!token) return;
    
    try {
      setProcessing(true);
      setError(null);
      
      await gamoraApi.declineInvitation(token);
      
      // Redirect to main app after declining
      navigate('/');
    } catch (err: any) {
      console.error('Failed to decline invitation:', err);
      if (err.response?.status === 400) {
        setError(err.response.data?.detail || 'Failed to decline invitation');
      } else {
        setError('Failed to decline invitation. Please try again.');
      }
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
          p: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 500,
            textAlign: 'center',
          }}
        >
          <Error sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" color="error" gutterBottom>
            Invitation Error
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            startIcon={<Business />}
          >
            Go to Gamura AI
          </Button>
        </Paper>
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
          p: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 500,
            textAlign: 'center',
          }}
        >
          <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" color="success.main" gutterBottom>
            Invitation Accepted!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You have successfully joined {invitation?.organization.name}. 
            You will be redirected to the main application shortly.
          </Typography>
          <CircularProgress size={24} />
        </Paper>
      </Box>
    );
  }

  if (!invitation) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 600,
          width: '100%',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Business sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            You're Invited!
          </Typography>
          <Typography variant="h6" color="primary.main" gutterBottom>
            Join {invitation.organization.name}
          </Typography>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Organization Details
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Name:</strong> {invitation.organization.name}
              </Typography>
              {invitation.organization.description && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Description:</strong> {invitation.organization.description}
                </Typography>
              )}
              {invitation.organization.industry && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Industry:</strong> {invitation.organization.industry}
                </Typography>
              )}
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Your Role:</strong> 
                <Chip 
                  label={invitation.role} 
                  color="primary" 
                  size="small" 
                  sx={{ ml: 1 }}
                />
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Invited by:</strong> {invitation.invited_by}
              </Typography>
            </Box>

            {invitation.message && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                  "{invitation.message}"
                </Typography>
              </>
            )}
          </CardContent>
        </Card>

        {expired && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            This invitation has expired. Please contact the organization administrator for a new invitation.
          </Alert>
        )}

        {!expired && (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={<PersonAdd />}
              onClick={handleAcceptInvitation}
              disabled={processing}
              sx={{ minWidth: 150 }}
            >
              {processing ? <CircularProgress size={20} /> : 'Accept Invitation'}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleDeclineInvitation}
              disabled={processing}
              sx={{ minWidth: 150 }}
            >
              {processing ? <CircularProgress size={20} /> : 'Decline'}
            </Button>
          </Box>
        )}

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="caption" color="text.secondary">
            This invitation expires on {new Date(invitation.expires_at).toLocaleDateString()}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default InvitationAccept;
